import { CookieSettingsLink } from "@/components/CookieConsent";
import { Link, useLocation } from "@/lib/router-compat";
import { useLanguage } from "@/i18n/LanguageContext";

import { getLocalizedPath } from "@/i18n/routes";

/**
 * Minimal legal-only footer for the private app area
 * (panel de usuario y back office). Does NOT render on
 * public marketing pages. On /dashboard it only renders
 * for logged-in users (guests see the full corporate Footer).
 */
const InternalFooter = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const es = lang === "es";

  const rawPath = location.pathname.replace(/\/+$/, "") || "/";

  // Las páginas en inglés viven bajo /en/... Para decidir si ya hay footer
  // corporativo, normalizamos la ruta a su equivalente canónico en español
  // reutilizando el mapa de src/i18n/routes.ts (única fuente de verdad).
  const canonicalPath = (() => {
    if (rawPath !== "/en" && !rawPath.startsWith("/en/")) return rawPath;
    const mapped = getLocalizedPath(rawPath, "es");
    if (mapped) return mapped;
    if (rawPath.startsWith("/en/insights/")) return rawPath.replace("/en/insights/", "/insights/");
    if (rawPath.startsWith("/en/guides/")) return rawPath.replace("/en/guides/", "/guias/");
    // Resto de rutas EN sin par registrado (p. ej. /en/dashboard): quitamos /en.
    return rawPath.slice(3) || "/";
  })();

  const path = canonicalPath;

  // Rutas que ya renderizan el Footer corporativo completo: NO duplicar.
  const PAGES_WITH_FULL_FOOTER = new Set<string>([
    "/",
    "/insights",
    "/mentoria",
    "/mentoria-retail",
    "/mentoring",
    "/contacto",
    "/contact",
    "/faq",
    "/en/faq",
    "/calculadora-rentabilidad-retail",
    "/herramientas/calculadora",
    "/en/tools/calculator",
    "/retail-profitability-calculator",
    "/panel/calculadora-pro",
    "/panel/pro-calculator",
    "/panel/subvenciones",
    "/panel/grants",
    "/panel/casos",
    "/panel/cases",

    "/aviso-legal",
    "/legal-notice",
    "/privacidad",
    "/privacy-policy",
    "/cookies",
    "/cookie-policy",
    "/condiciones-contratacion",
    "/terms-of-service",
    "/termometro-lemon",
    "/lemon-thermometer",

    "/cadenas-y-franquicias",
    "/family-offices-inversores",
    // Secciones de landing promovidas a página propia (llevan Footer completo)
    "/como-funciona",
    "/estrategia",
    "/agente-ia",
    "/tarifas",
    "/diagnostico-locales-comerciales",
    "/diagnosticador-locales",
    "/todo-para-tu-comercio",
    "/problemas-en-mi-tienda",
    "/abrir-tienda-en-espana",

  ]);


  const hasFullFooter =
    PAGES_WITH_FULL_FOOTER.has(path) ||
    path.startsWith("/insights/") ||
    path.startsWith("/guias/");

  // Dashboard owns its public footer and its private loading shell; rendering a
  // second global footer here made the mobile loader scroll to a legal block.
  if (path === "/dashboard" || rawPath === "/admin" || rawPath.startsWith("/admin/")) {
    return null;
  } else if (hasFullFooter) {
    return null;
  }


  const links = es
    ? [
        { to: "/aviso-legal", label: "Aviso Legal" },
        { to: "/privacidad", label: "Privacidad" },
        { to: "/cookies", label: "Cookies" },
        { to: "/condiciones-contratacion", label: "Condiciones" },
        { to: "/faq", label: "FAQ" },
      ]
    : [
        { to: "/en/legal-notice", label: "Legal Notice" },
        { to: "/en/privacy-policy", label: "Privacy" },
        { to: "/en/cookies", label: "Cookies" },
        { to: "/en/terms", label: "Terms" },
        { to: "/en/faq", label: "FAQ" },
      ];

  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-50/60 mt-auto">


      {/* Legal bottom bar */}
      <div className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 text-xs text-muted-foreground font-body">
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1 text-center">
            <span>
              © {year} Retail Lemon. {es ? "Todos los derechos reservados." : "All rights reserved."}
            </span>
            {links.map((l) => (
              <span key={l.to} className="flex items-center gap-x-1.5">
                <span aria-hidden="true">|</span>
                <Link to={l.to} className="hover:text-slate-600 transition-colors">
                  {l.label}
                </Link>
              </span>
            ))}
            <span className="flex items-center gap-x-1.5">
              <span aria-hidden="true">|</span>
              <CookieSettingsLink className="hover:text-slate-600 transition-colors" />
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default InternalFooter;
